import React, { useState, useMemo } from "react";
import {
  Card,
  CardHeader,
  Typography,
  Box,
  Stack,
  IconButton,
  Divider,
} from "@mui/material";
import { useGetMembersQuery } from "../../../features/members/membersApiSlice";
import { fDate } from "../../../utils/formatTime";
import Iconify from "../../../components/iconify";
import Scrollbar from "../../../components/scrollbar";

const monthNames = [
  "Januar",
  "Februar",
  "März",
  "April",
  "Mai",
  "Juni",
  "Juli",
  "August",
  "September",
  "Oktober",
  "November",
  "Dezember",
];

function calculateAge(birthdate, monthOffset = 0) {
  const birth = new Date(birthdate);
  const today = new Date();
  const thisYear = today.getFullYear();
  const targetDate = new Date(
    thisYear,
    today.getMonth() + monthOffset,
    birth.getDate()
  );
  let age = thisYear - birth.getFullYear();
  if (targetDate < today) age += 1;
  return age;
}

export default function BirthdayOverview() {
  const { data: members = [] } = useGetMembersQuery();
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());

  const handleMonthChange = (direction) => {
    setSelectedMonth((prev) => (prev + direction + 12) % 12);
  };

  const today = new Date();

  const birthdayList = useMemo(() => {
    return members
      .filter((m) => m.status === "active" && m.birthdate)
      .map((m) => ({
        ...m,
        birthday: new Date(m.birthdate),
      }))
      .filter((m) => m.birthday.getMonth() === selectedMonth)
      .sort((a, b) => a.birthday.getDate() - b.birthday.getDate());
  }, [members, selectedMonth]);

  return (
    <Card>
      <CardHeader
        title={`Geburtstage im ${monthNames[selectedMonth]}`}
        action={
          <Stack direction="row" spacing={1}>
            <IconButton onClick={() => handleMonthChange(-1)}>
              <Iconify icon="eva:arrow-ios-back-fill" />
            </IconButton>
            <IconButton onClick={() => handleMonthChange(1)}>
              <Iconify icon="eva:arrow-ios-forward-fill" />
            </IconButton>
          </Stack>
        }
      />

      <Divider />

      <Scrollbar sx={{ maxHeight: 450 }}>
        <Stack spacing={2} sx={{ p: 3, pr: 2 }}>
          {birthdayList.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              Keine Geburtstage in diesem Monat.
            </Typography>
          )}

          {birthdayList.map((m, i) => {
            const isToday =
              m.birthday.getDate() === today.getDate() &&
              m.birthday.getMonth() === today.getMonth();
            const age = calculateAge(m.birthday);

            return (
              <Stack
                key={m._id}
                direction="row"
                justifyContent="start"
                alignItems="center"
                sx={{
                  p: 0,
                  borderRadius: 1,
                  bgcolor: isToday ? "primary.lighter" : "background.paper",
                  boxShadow: isToday ? 3 : 0,
                }}
              >
                {isToday && (
                  <Iconify width={45} icon="emojione-v1:birthday-cake" />
                )}
                <Box sx={{ ml: 2 }}>
                  <Typography variant="subtitle1" noWrap>
                    {m.firstName} {m.lastName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {fDate(m.birthday)} — wird {age} Jahre alt
                  </Typography>
                </Box>
              </Stack>
            );
          })}
        </Stack>
      </Scrollbar>
    </Card>
  );
}
